<script setup lang="ts">
import { ref, computed } from 'vue'

// Define props for visibility control
const emit = defineEmits<{
  (e: 'close'): void
}>()

interface TutorialStep {
  imageUrl: string
  description: string
}

const steps: TutorialStep[] = [
  {
    imageUrl: './images/HowItWorks1.webp',
    description: 'Vítejte! Nejprve klikněte na tlačítko "Přidejte Poetické místo" v horní liště.',
  },
  {
    imageUrl: './images/HowItWorks2.webp',
    description:
      'Najděte místo na mapě Prahy, ke kterému se váže vaše báseň nebo příběh, a klikněte na něj.',
  },
  {
    imageUrl: './images/HowItWorks3.webp',
    description: 'Vyplňte detaily a klikněte na "Přidejte Poetické místo".',
  },
]

const currentStep = ref<number>(0)

const currentStepData = computed<TutorialStep>(() => steps[currentStep.value])

const closeWindow = () => {
  emit('close')
}

const nextStep = () => {
  if (currentStep.value < steps.length - 1) {
    currentStep.value++
  }
}

const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}
</script>
<template>
  <div class="modal-overlay" @click.self="closeWindow">
    <div class="modal-window">
      <button class="close-btn" @click="closeWindow" aria-label="Zavřít">&#10005;</button>

      <div class="image-area">
        <img
          v-if="currentStepData.imageUrl"
          :src="currentStepData.imageUrl"
          :alt="`Krok ${currentStep + 1}`"
        />
        <div v-else class="image-placeholder">Zástupný obrázek pro Krok {{ currentStep + 1 }}</div>
      </div>

      <div class="bottom-bar">
        <button
          class="nav-btn"
          :class="{ invisible: currentStep === 0 }"
          @click="prevStep"
          aria-label="Předchozí krok"
        >
          &#8592;
        </button>

        <div class="description-area">
          <p>
            <strong>Krok {{ currentStep + 1 }} z {{ steps.length }}:</strong>
          </p>
          <p>{{ currentStepData.description }}</p>
        </div>

        <button
          class="nav-btn"
          :class="{ invisible: currentStep === steps.length - 1 }"
          @click="nextStep"
          aria-label="Další krok"
        >
          &#8594;
        </button>
      </div>
    </div>
  </div>
</template>
<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;

  z-index: 100;
}

.modal-window {
  position: relative;
  width: 85%;
  height: 85%;
  max-width: 1100px;
  background-color: #fdfbf7;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 25px;
  background: transparent;
  border: none;
  color: #000000;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
  z-index: 10;
  padding: 5px;
  line-height: 1;
  transition:
    transform 0.2s ease,
    color 0.2s ease;
}

.close-btn:hover {
  transform: scale(1.15);
  color: #f2a900;
}

.image-area {
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f4f1ea;
  padding: 40px;
  overflow: hidden;
}

.image-area img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.image-placeholder {
  color: #1a2432;
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
}

.bottom-bar {
  height: 130px;
  background-color: #fdfbf7;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
}

.nav-btn {
  background: transparent;
  border: none;
  font-size: 40px;
  color: #1a2432;
  cursor: pointer;
  padding: 10px;
  transition:
    transform 0.2s ease,
    color 0.2s ease;
}

.nav-btn:hover:not(.invisible) {
  transform: scale(1.1);
  color: #f2a900;
}

.nav-btn.invisible {
  opacity: 0;
  pointer-events: none;
}

.description-area {
  text-align: center;
  max-width: 60%;
  color: #1a2432;
  font-family: 'Inter', sans-serif;
}

.description-area p {
  margin: 6px 0;
  font-size: 1.1rem;
  line-height: 1.5;
}

.description-area strong {
  font-weight: 600;
  color: #f2a900;
}
</style>
